// src/features/tag/useTagUsage.ts

import { useQuery } from '@tanstack/react-query';
import { TagApi } from './TagApi';
import { TAG_QUERY_KEYS } from '@/lib/constants/tag';
import { useAuth } from '@/features/auth/hooks/useAuth';
import type { TagUsage } from '@/lib/types/Tag';

export interface UseTagUsageResult {
  usages: TagUsage[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Account tag vocabulary with usage counts (Manage Tags drawer).
 * Server returns rows sorted by label; short staleTime because counts go
 * stale with every media/tag edit.
 */
export function useTagUsage(options?: { enabled?: boolean }): UseTagUsageResult {
  const { selectedCId } = useAuth();

  const query = useQuery({
    queryKey: TAG_QUERY_KEYS.usage(selectedCId ?? 0),
    queryFn: async () => {
      const response = await TagApi.getTagUsage(selectedCId!);
      // Wire tolerance: schedule/ingest-config counts arrived with api _20260715a;
      // an older api omits them and undefined must read as 0, not "in use".
      return response.tags.map((t) => ({
        ...t,
        scheduleCount: t.scheduleCount ?? 0,
        ingestCfgCount: t.ingestCfgCount ?? 0,
      }));
    },
    enabled: selectedCId != null && (options?.enabled ?? true),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    usages: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
